import { ContentState, EditorState, convertToRaw } from "draft-js";
import htmlToDraft from "html-to-draftjs";
import draftToHtml from "draftjs-to-html";

const asArray = (value) => {
  if (Array.isArray(value)) return value;
  if (value === null || value === undefined || value === "") return [];
  return [value];
};

const toStringValue = (value) => {
  if (value === null || value === undefined) return "";
  return String(value).trim();
};

const toNumber = (value, fallback = 0) => {
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : fallback;
};

const toBoolean = (value, fallback = false) => {
  if (typeof value === "boolean") return value;
  if (typeof value === "number") return value === 1;
  if (typeof value === "string") {
    const normalized = value.trim().toLowerCase();
    if (["true", "1", "yes", "on"].includes(normalized)) return true;
    if (["false", "0", "no", "off"].includes(normalized)) return false;
  }
  return fallback;
};

const slugify = (value = "") =>
  value
    .toLowerCase()
    .trim()
    .replace(/[^a-z0-9\s-]/g, "")
    .replace(/\s+/g, "-")
    .replace(/-+/g, "-")
    .replace(/^-|-$/g, "");

const normalizeReference = (value, fallbackId = "", fallbackName = "") => {
  if (value && typeof value === "object" && !Array.isArray(value)) {
    return {
      id: toStringValue(value.id || value.value || value._id || fallbackId),
      name: toStringValue(value.name || value.label || fallbackName),
    };
  }

  return {
    id: toStringValue(fallbackId),
    name: toStringValue(fallbackName),
  };
};

const emptyVideoItem = () => ({ title: "", url: "" });

const makeDraftEditorState = (html) => {
  if (!toStringValue(html)) {
    return EditorState.createEmpty();
  }

  const blocksFromHtml = htmlToDraft(html);
  const { contentBlocks, entityMap } = blocksFromHtml;
  const contentState = ContentState.createFromBlockArray(contentBlocks, entityMap);
  return EditorState.createWithContent(contentState);
};

const normalizeVideoUrlsForForm = (product = {}) => {
  const fromNew = asArray(product.video_urls);
  const fromLegacy = asArray(product.videoUrl);
  const source = fromNew.length > 0 ? fromNew : fromLegacy;

  const list = source
    .map((item, index) => {
      if (item && typeof item === "object" && !Array.isArray(item)) {
        return {
          title: toStringValue(item.title || item.label || `Video ${index + 1}`),
          url: toStringValue(item.url || item.value),
        };
      }

      return {
        title: `Video ${index + 1}`,
        url: toStringValue(item),
      };
    })
    .filter((item) => item.url);

  return list.length > 0 ? list : [emptyVideoItem()];
};

const normalizeSizeVariantsForForm = (colorVariant = {}, product = {}) => {
  const fromNew = asArray(colorVariant.size_variants).map((sizeVariant, index) => ({
    variant_id: Number(sizeVariant.variant_id || sizeVariant.variantId || Date.now() + index),
    size_id: toStringValue(sizeVariant.size_id || sizeVariant.sizeId || sizeVariant.value),
    size_name: toStringValue(sizeVariant.size_name || sizeVariant.sizeName || sizeVariant.label),
    sku: toStringValue(sizeVariant.sku),
    price: toNumber(sizeVariant.price, toNumber(product.mrp, 0)),
    discount: toNumber(sizeVariant.discount, 0),
    stock: Math.max(0, parseInt(sizeVariant.stock, 10) || 0),
    weight: toStringValue(sizeVariant.weight),
    is_default: toBoolean(sizeVariant.is_default, index === 0),
    is_active: toBoolean(sizeVariant.is_active, true),
  }));

  if (fromNew.length > 0) {
    return ensureSingleDefault(fromNew, "is_default");
  }

  const legacySizes = asArray(product.size);
  if (legacySizes.length > 0) {
    return ensureSingleDefault(
      legacySizes.map((legacySize, index) => ({
        variant_id: Date.now() + index,
        size_id: toStringValue(legacySize.value),
        size_name: toStringValue(legacySize.label),
        sku: "",
        price: toNumber(product.mrp, 0),
        discount: Math.max(0, toNumber(product.mrp, 0) - toNumber(product.regularDiscount, 0)),
        stock: Math.max(0, parseInt(product.availableQuantity, 10) || 0),
        weight: "",
        is_default: index === 0,
        is_active: true,
      })),
      "is_default"
    );
  }

  return [
    {
      variant_id: Date.now(),
      size_id: "",
      size_name: "",
      sku: "",
      price: toNumber(product.mrp, 0),
      discount: Math.max(0, toNumber(product.mrp, 0) - toNumber(product.regularDiscount, 0)),
      stock: Math.max(0, parseInt(product.availableQuantity, 10) || 0),
      weight: "",
      is_default: true,
      is_active: true,
    },
  ];
};

const normalizeColorVariantsForForm = (product = {}) => {
  const fromNew = asArray(product.color_variants).map((colorVariant, index) => {
    const assets = asArray(colorVariant.color_image_assets)
      .map((asset) => ({
        url: toStringValue(asset?.url),
        publicId: toStringValue(asset?.public_id || asset?.publicId),
      }))
      .filter((asset) => asset.url || asset.publicId);

    const images = asArray(colorVariant.color_images)
      .map((url) => toStringValue(url))
      .filter(Boolean);

    if (assets.length === 0 && images.length > 0) {
      images.forEach((url) => {
        assets.push({
          url,
          publicId: "",
        });
      });
    }

    return {
      color_id: toStringValue(colorVariant.color_id || colorVariant.colorId),
      color_name: toStringValue(colorVariant.color_name || colorVariant.colorName),
      color_hex_code: toStringValue(colorVariant.color_hex_code || colorVariant.colorHexCode),
      color_images: images,
      color_image_assets: assets,
      is_default: toBoolean(colorVariant.is_default, index === 0),
      size_variants: normalizeSizeVariantsForForm(colorVariant, product),
    };
  });

  if (fromNew.length > 0) {
    return ensureSingleDefault(fromNew, "is_default");
  }

  const fromLegacy = asArray(product.productImgColor).map((item, index) => ({
    color_id: toStringValue(item.colorId),
    color_name: toStringValue(item.colorName),
    color_hex_code: toStringValue(item.colorHexCode),
    color_images: [toStringValue(item.url)].filter(Boolean),
    color_image_assets: [
      {
        url: toStringValue(item.url),
        publicId: toStringValue(item.publicId),
      },
    ].filter((asset) => asset.url || asset.publicId),
    is_default: index === 0,
    size_variants: normalizeSizeVariantsForForm({}, product),
  }));

  return ensureSingleDefault(fromLegacy, "is_default");
};

const normalizeRelatedProducts = (product = {}) => {
  const fromNew = asArray(product.related_products)
    .map((item) => {
      if (item && typeof item === "object" && !Array.isArray(item)) {
        return toStringValue(item._id || item.id || item.value);
      }
      return toStringValue(item);
    })
    .filter(Boolean);

  if (fromNew.length > 0) {
    return [...new Set(fromNew)];
  }

  const fromLegacy = asArray(product.relatedProducts)
    .map((item) => {
      if (item && typeof item === "object") {
        if (item.value && typeof item.value === "object") {
          return toStringValue(item.value._id || item.value.id || item.value.value);
        }
        return toStringValue(item.value || item._id || item.id);
      }
      return toStringValue(item);
    })
    .filter(Boolean);

  return [...new Set(fromLegacy)];
};

export const createInitialProductInput = () => ({
  product_name: "",
  slug: "",
  thumbnail: "",
  thumbnail_public_id: "",
  gallery: [],
  short_description: "",
  full_description: "",
  longDescriptionView: EditorState.createEmpty(),
  video_urls: [emptyVideoItem()],
  category: { id: "", name: "" },
  sub_category: { id: "", name: "" },
  sub_sub_category: { id: "", name: "" },
  brand: { id: "", name: "" },
  seller: { id: "", name: "" },
  related_products: [],
  is_cash_on_delivery: true,
  is_free_delivery: false,
  is_trending: false,
  is_active: true,
  color_variants: [],

  productIcon: { url: "", publicId: null },
  productImg: { url: "", publicId: null },

  draftColorId: "",
  draftColorName: "",
  draftColorHexCode: "",
  draftColorIsDefault: false,
  draftColorImages: [],
  draftSizeVariants: [],
  draftSizeId: "",
  draftSizeName: "",
  draftSku: "",
  draftPrice: "",
  draftDiscount: "",
  draftStock: "",
  draftWeight: "",
  draftSizeIsDefault: false,
  draftSizeIsActive: true,
});

export const ensureSingleDefault = (items = [], key = "is_default") => {
  if (!Array.isArray(items) || items.length === 0) {
    return [];
  }

  let defaultIndex = items.findIndex((item) => item[key] === true);
  if (defaultIndex < 0) {
    defaultIndex = 0;
  }

  return items.map((item, index) => ({
    ...item,
    [key]: index === defaultIndex,
  }));
};

export const normalizeProductForForm = (product = {}) => {
  const initial = createInitialProductInput();
  if (!product || typeof product !== "object") {
    return initial;
  }

  const productName = toStringValue(product.product_name || product.productName);
  const slug = toStringValue(product.slug) || slugify(productName);
  const thumbnail = toStringValue(product.thumbnail || product.productIcon?.url);
  const thumbnailPublicId = toStringValue(product.thumbnail_public_id || product.productIcon?.publicId);
  const shortDescription = toStringValue(product.short_description || product.shortDescriptions);
  const fullDescription = toStringValue(product.full_description || product.longDescriptions);

  const gallery = asArray(product.gallery)
    .map((url) => toStringValue(url))
    .filter(Boolean);

  return {
    ...initial,
    product_name: productName,
    slug,
    thumbnail,
    thumbnail_public_id: thumbnailPublicId,
    gallery,
    short_description: shortDescription,
    full_description: fullDescription,
    longDescriptionView: makeDraftEditorState(fullDescription),
    video_urls: normalizeVideoUrlsForForm(product),
    category: normalizeReference(product.category, product.categoryId, product.categoryName),
    sub_category: normalizeReference(product.sub_category, product.subCategoryId, product.subCategoryName),
    sub_sub_category: normalizeReference(product.sub_sub_category, product.subSubCategoryId, product.subSubCategoryName),
    brand: normalizeReference(product.brand, product.brandId, product.brandName),
    seller: normalizeReference(product.seller, product.sellerId, product.sellerName),
    related_products: normalizeRelatedProducts(product),
    is_cash_on_delivery: toBoolean(
      product.is_cash_on_delivery !== undefined
        ? product.is_cash_on_delivery
        : product.isAvailableCashOnDelivery,
      true
    ),
    is_free_delivery: toBoolean(
      product.is_free_delivery !== undefined ? product.is_free_delivery : product.isFreeDelivery,
      false
    ),
    is_trending: toBoolean(product.is_trending !== undefined ? product.is_trending : product.isTrending, false),
    is_active: toBoolean(product.is_active !== undefined ? product.is_active : product.isActive, true),
    color_variants: normalizeColorVariantsForForm(product),
    productIcon: {
      url: thumbnail,
      publicId: thumbnailPublicId || null,
    },
  };
};

const isEditorStateEmpty = (editorState) => {
  if (!editorState || !editorState.getCurrentContent) {
    return true;
  }

  const raw = convertToRaw(editorState.getCurrentContent());
  if (!raw || !Array.isArray(raw.blocks)) {
    return true;
  }

  return raw.blocks.every((block) => !toStringValue(block.text));
};

export const validateProductInput = (input = {}) => {
  const errors = getProductValidationErrors(input);
  return errors.length > 0 ? errors[0].message : "";
};

export const getProductValidationErrors = (input = {}) => {
  const errors = [];

  const pushError = (field, message) => {
    const alreadyExists = errors.some(
      (error) => error.field === field && error.message === message
    );
    if (!alreadyExists) {
      errors.push({ field, message });
    }
  };

  if (!toStringValue(input.product_name)) {
    pushError("product_name", "Product name is required");
  }

  if (!toStringValue(input.thumbnail || input.productIcon?.url)) {
    pushError("thumbnail", "Thumbnail is required");
  }

  if (!toStringValue(input.short_description)) {
    pushError("short_description", "Short description is required");
  }

  const hasFullDescriptionText = !isEditorStateEmpty(input.longDescriptionView);
  const hasFallbackFullDescription = toStringValue(input.full_description).length > 0;
  if (!hasFullDescriptionText && !hasFallbackFullDescription) {
    pushError("full_description", "Full description is required");
  }

  if (!toStringValue(input.category?.id)) {
    pushError("category", "Category is required");
  }

  if (!toStringValue(input.seller?.id)) {
    pushError("seller", "Seller is required");
  }

  const colorVariants = asArray(input.color_variants);
  if (colorVariants.length === 0) {
    pushError("color_variants", "Add at least one color variant");
    pushError("variant_color", "Color is required in variant builder");
    pushError("variant_color_image", "Upload color image is required in variant builder");
    pushError("variant_size", "Add at least one size variant in variant builder");
    pushError("variant_price", "Price is required in size variant");
    pushError("variant_stock", "Stock is required in size variant");
  }

  colorVariants.forEach((colorVariant, colorIndex) => {
    const variantNo = colorIndex + 1;

    if (!toStringValue(colorVariant.color_id)) {
      pushError("variant_color", `Color is required in variant ${variantNo}`);
    }

    const images = asArray(colorVariant.color_images).filter(Boolean);
    if (images.length === 0) {
      pushError("variant_color_image", `Upload at least one color image in variant ${variantNo}`);
    }

    const sizeVariants = asArray(colorVariant.size_variants);
    if (sizeVariants.length === 0) {
      pushError("variant_size", `Add at least one size variant in variant ${variantNo}`);
      pushError("variant_price", `Price is required in variant ${variantNo}`);
      pushError("variant_stock", `Stock is required in variant ${variantNo}`);
    }

    sizeVariants.forEach((sizeVariant, sizeIndex) => {
      const rowNo = sizeIndex + 1;
      if (toNumber(sizeVariant.price, 0) <= 0) {
        pushError(
          "variant_price",
          `Price must be greater than 0 in variant ${variantNo}, size row ${rowNo}`
        );
      }

      const stockValue = Number(sizeVariant.stock);
      if (!Number.isFinite(stockValue) || stockValue < 0) {
        pushError(
          "variant_stock",
          `Stock must be 0 or more in variant ${variantNo}, size row ${rowNo}`
        );
      }
    });
  });

  return errors;
};

const sanitizeVideoUrls = (videoUrls = []) =>
  asArray(videoUrls)
    .map((item, index) => ({
      title: toStringValue(item?.title || `Video ${index + 1}`),
      url: toStringValue(item?.url),
    }))
    .filter((item) => item.url);

const sanitizeSizeVariants = (sizeVariants = []) =>
  ensureSingleDefault(
    asArray(sizeVariants).map((sizeVariant, index) => ({
      variant_id: Number(sizeVariant.variant_id || Date.now() + index),
      size_id: toStringValue(sizeVariant.size_id),
      size_name: toStringValue(sizeVariant.size_name || sizeVariant.size_id),
      sku: toStringValue(sizeVariant.sku),
      price: Math.max(0, toNumber(sizeVariant.price, 0)),
      discount: Math.max(0, toNumber(sizeVariant.discount, 0)),
      stock: Math.max(0, parseInt(sizeVariant.stock, 10) || 0),
      weight: toStringValue(sizeVariant.weight),
      is_default: toBoolean(sizeVariant.is_default, index === 0),
      is_active: toBoolean(sizeVariant.is_active, true),
    })),
    "is_default"
  );

const sanitizeColorVariants = (colorVariants = []) =>
  ensureSingleDefault(
    asArray(colorVariants).map((colorVariant, index) => {
      const assets = asArray(colorVariant.color_image_assets)
        .map((asset) => ({
          url: toStringValue(asset?.url),
          public_id: toStringValue(asset?.public_id || asset?.publicId),
          publicId: toStringValue(asset?.publicId || asset?.public_id),
        }))
        .filter((asset) => asset.url || asset.public_id || asset.publicId);

      const colorImages = [
        ...new Set(
          asArray(colorVariant.color_images)
            .map((url) => toStringValue(url))
            .filter(Boolean)
        ),
      ];

      return {
        color_id: toStringValue(colorVariant.color_id),
        color_name: toStringValue(colorVariant.color_name),
        color_hex_code: toStringValue(colorVariant.color_hex_code),
        color_images: colorImages,
        color_image_assets: assets,
        is_default: toBoolean(colorVariant.is_default, index === 0),
        size_variants: sanitizeSizeVariants(colorVariant.size_variants),
      };
    }),
    "is_default"
  );

export const prepareProductPayload = (input = {}) => {
  const productName = toStringValue(input.product_name);
  const slug = toStringValue(input.slug) || slugify(productName);

  const fullDescriptionFromEditor =
    input.longDescriptionView && input.longDescriptionView.getCurrentContent
      ? draftToHtml(convertToRaw(input.longDescriptionView.getCurrentContent()))
      : "";

  const fullDescription =
    toStringValue(fullDescriptionFromEditor) || toStringValue(input.full_description);

  const colorVariants = sanitizeColorVariants(input.color_variants);

  const gallery = [
    ...new Set(
      [
        ...asArray(input.gallery),
        ...colorVariants.flatMap((variant) => asArray(variant.color_images)),
      ]
        .map((item) => toStringValue(item))
        .filter(Boolean)
    ),
  ];

  const thumbnail =
    toStringValue(input.thumbnail || input.productIcon?.url) ||
    toStringValue(colorVariants?.[0]?.color_images?.[0]);

  const thumbnailPublicId = toStringValue(
    input.thumbnail_public_id ||
    input.productIcon?.publicId ||
    colorVariants?.[0]?.color_image_assets?.[0]?.public_id ||
    colorVariants?.[0]?.color_image_assets?.[0]?.publicId
  );

  return {
    product_name: productName,
    slug,
    thumbnail,
    thumbnail_public_id: thumbnailPublicId,
    gallery,
    short_description: toStringValue(input.short_description),
    full_description: fullDescription,
    video_urls: sanitizeVideoUrls(input.video_urls),
    category: {
      id: toStringValue(input.category?.id),
      name: toStringValue(input.category?.name),
    },
    sub_category: {
      id: toStringValue(input.sub_category?.id),
      name: toStringValue(input.sub_category?.name),
    },
    sub_sub_category: {
      id: toStringValue(input.sub_sub_category?.id),
      name: toStringValue(input.sub_sub_category?.name),
    },
    brand: {
      id: toStringValue(input.brand?.id),
      name: toStringValue(input.brand?.name),
    },
    seller: {
      id: toStringValue(input.seller?.id),
      name: toStringValue(input.seller?.name),
    },
    related_products: [...new Set(asArray(input.related_products).map((id) => toStringValue(id)).filter(Boolean))],
    is_cash_on_delivery: toBoolean(input.is_cash_on_delivery, true),
    is_free_delivery: toBoolean(input.is_free_delivery, false),
    is_trending: toBoolean(input.is_trending, false),
    is_active: toBoolean(input.is_active, true),
    color_variants: colorVariants,
    productIcon: {
      url: thumbnail,
      publicId: thumbnailPublicId,
    },
  };
};

export const getColorVariantsFromProduct = (product = {}) => {
  const colorVariants = normalizeColorVariantsForForm(product);
  return colorVariants;
};

export const getDefaultSizeVariantFromProduct = (product = {}) => {
  const colorVariants = getColorVariantsFromProduct(product);
  const defaultColor = colorVariants.find((variant) => variant.is_default) || colorVariants[0];
  if (!defaultColor) return null;

  const sizeVariants = asArray(defaultColor.size_variants);
  return sizeVariants.find((variant) => variant.is_default) || sizeVariants[0] || null;
};

export const getProductSummaryMetrics = (product = {}) => {
  const colorVariants = getColorVariantsFromProduct(product);
  const sizeCount = colorVariants.reduce(
    (total, colorVariant) => total + asArray(colorVariant.size_variants).length,
    0
  );
  const totalStock = colorVariants.reduce(
    (stockAccumulator, colorVariant) =>
      stockAccumulator +
      asArray(colorVariant.size_variants).reduce(
        (sizeStockAccumulator, sizeVariant) => sizeStockAccumulator + Math.max(0, parseInt(sizeVariant.stock, 10) || 0),
        0
      ),
    0
  );
  const defaultSizeVariant = getDefaultSizeVariantFromProduct(product);

  return {
    colorCount: colorVariants.length,
    sizeCount,
    totalStock,
    defaultPrice: toNumber(defaultSizeVariant?.price, toNumber(product.mrp, 0)),
    defaultDiscount: toNumber(defaultSizeVariant?.discount, 0),
  };
};

export const getDisplayProductName = (product = {}) =>
  toStringValue(product.product_name || product.productName);
